import {ShopperProducts} from 'commerce-api'
import {ProductModel} from '../../store/ProductStore'
import {ItemPrices} from '../../types/item-prices'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {capitalize, formatProductName} from '../utils'
import {SeasonalHours, StoreDetailResponseItem} from '../../types/store/storeDetails'
import {DateUtils} from '../date-utils'

export type StructuredListItem = {
  '@type': 'ListItem'
  position: number
  item: {
    '@id': string | undefined
    name: string | undefined
  }
}

export type StructuredBreadcrumbList = {
  '@context': 'https://schema.org'
  '@type': 'BreadcrumbList'
  itemListElement: Array<StructuredListItem>
}

export type PriceSpecification = {
  '@type': 'UnitPriceSpecification'
  price: number | null
  priceCurrency: string | undefined
  priceType?: string
}

export type StructuredOffer = {
  '@type': 'Offer'
  availability: 'https://schema.org/InStock'
  priceSpecification?: PriceSpecification | PriceSpecification[]
}

export type Rating = {
  bestRating: string
  ratingValue: string
  worstRating: string
}

export type Review = {
  author: string
  datePublished: string
  reviewBody: string
  name: string
  reviewRating: Rating
}

export type StructuredRating = Rating & {
  '@type': 'Rating'
}

export type StructuredReview = Review & {
  '@type': 'Review'
}

export type StructuredAggregateRating = {
  '@type': 'AggregateRating'
  ratingValue: string
  reviewCount: string
}

export type StructuredProduct = {
  '@type': 'Product'
  image: string | Record<string, string | Record<string, string>> | undefined
  url: string
  name: string | undefined
  brand?: string
  offers: StructuredOffer
  aggregateRating?: StructuredAggregateRating
  reviews?: StructuredReview[]
}

type StructuredEntryPoint = {
  '@type': 'EntryPoint'
  urlTemplate: string
}

type StructuredBaseAction = {
  '@type': 'Action'
  name: string
  target: StructuredEntryPoint
}

type StructuredSearchAction = Omit<StructuredBaseAction, '@type'> & {
  '@type': 'SearchAction'
  query?: string
}

type StructuredTradeAction = Omit<StructuredBaseAction, '@type'> & {
  '@type': 'TradeAction'
}

type StructuredFindAction = Omit<StructuredBaseAction, '@type'> & {
  '@type': 'FindAction'
}

type StructuredAction = StructuredBaseAction | StructuredSearchAction | StructuredTradeAction | StructuredFindAction

export type StructuredWebsite = {
  "@context": "https://schema.org"
  "@type": "WebSite"
  "@id": string
  url: string
  name: string
  potentialAction?: Array<StructuredAction>
}

type StructuredContactPoint = {
  "@type": "ContactPoint"
  telephone: string
  contactType: string
  areaServed: string
  availableLanguage: string
}

export type StructuredOnlineStore = {
  "@context": "https://schema.org"
  "@type": "OnlineStore"
  "@id": string
  name: string
  url: string
  logo: string
  sameAs?:  string[]
  contactPoint?: StructuredContactPoint
}

export type StructuredItemList<ItemT> = {
  '@context': 'https://schema.org'
  '@type': 'ItemList'
  url: string
  numberOfItems: string
  itemListElement: Array<ItemT>
}

type StructuredPostalAddress = {
  '@type': 'PostalAddress'
  streetAddress?: string
  addressLocality?: string
  addressRegion?: string
  postalCode?: string
  addressCountry?: string
}

type StructuedGeoCoordinates = {
  '@type': 'GeoCoordinates'
  latitude: number
  longitude: number
}

type StructuredOpeningHoursSpecification = {
  '@type': 'OpeningHoursSpecification'
  dayOfWeek: string | string[]
  opens?: string
  closes?: string
  validFrom?: string
  validThrough?: string
  description?: string
}

type StructuredOrganization = {
  '@type': 'Organization'
  '@id': string
}

export type StructuredGroceryStore = {
  '@context': 'https://schema.org'
  '@type': 'GroceryStore'
  '@id': string
  name: string
  url: string
  telephone?: string
  image?: string
  address: StructuredPostalAddress
  geo: StructuedGeoCoordinates
  priceRange: string
  openingHoursSpecification: StructuredOpeningHoursSpecification[]
  specialOpeningHoursSpecification?: StructuredOpeningHoursSpecification[]
  parentOrganization?: StructuredOrganization
}

export type StructuredProductList = StructuredItemList<StructuredProduct>

export type StructuredDataItem =
  | StructuredBreadcrumbList
  | StructuredItemList<StructuredProductList>
  | StructuredProduct
  | StructuredGroceryStore
  | StructuredWebsite
  | StructuredOnlineStore

type StructuredDataType = Array<StructuredDataItem>

export default StructuredDataType

export const buildListItemStructuredData = (
  listItem: ShopperProducts.PathRecord,
  position: number
): StructuredListItem => ({
  '@type': 'ListItem',
  position: position,
  item: {
    '@id': listItem.id,
    name: listItem.name
  }
})

export const buildBreadcrumbsStructuredData = (crumbs: any): StructuredBreadcrumbList => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      ...(crumbs || []).map((listItem: ShopperProducts.PathRecord, i: number) =>
        buildListItemStructuredData(
          {
            id: listItem.url,
            name: listItem.name
          },
          i + 2
        )
      )
    ]
  }
}

export const buildStructuredOfferData = (
  price: number | null,
  currency: string,
  wasPrice: number | null
): StructuredOffer => {
  const offers: StructuredOffer = {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock'
  }
  
  if (price !== null) {
    if (wasPrice !== null && wasPrice !== price) {
      offers.priceSpecification = [
        {
          '@type': 'UnitPriceSpecification',
          price,
          priceCurrency: currency
        },
        {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/StrikethroughPrice',
          price: wasPrice,
          priceCurrency: currency
        }
      ]
    } else {
      offers.priceSpecification = {
        '@type': 'UnitPriceSpecification',
        price,
        priceCurrency: currency
      }
    }
  }

  return offers
}

export const buildStructuredAggregateRatingData = (
  productRating: number,
  productReviewCount: number
): StructuredAggregateRating => {
  return {
    '@type': 'AggregateRating',
    ratingValue: productRating?.toString(),
    reviewCount: productReviewCount?.toString()
  }
}

export const buildStructuredRatingData = ({
  bestRating,
  ratingValue,
  worstRating
}: Rating): StructuredRating => ({
  '@type': 'Rating',
  bestRating,
  ratingValue,
  worstRating
})

export const buildStructuredReviewData = ({
  author,
  datePublished,
  reviewBody,
  name,
  reviewRating
}: Review): StructuredReview => ({
  '@type': 'Review',
  author,
  datePublished,
  reviewBody,
  name,
  reviewRating: buildStructuredRatingData(reviewRating)
})

export const buildProductModelStructured = (
  product: ProductModel,
  priceForData: number | null,
  wasPriceforData: number | null
): StructuredProduct => {
  const {image_groups, name, id, brand, imageGroups, productRating, productReviewCount} = product
  const url = `${getAppOrigin()}/p/${formatProductName(name)}/${id}.html`
  const image = image_groups?.[0]?.images?.[0] || imageGroups?.[0]?.images?.[0]

  return {
    '@type': 'Product',
    image: {
      '@type': 'ImageObject',
      contentUrl: image?.dis_base_link,
      license: 'https://iceland.co.uk',
      acquireLicensePage: 'https://iceland.co.uk',
      creditText: 'Iceland',
      creator: {
        '@type': 'Organization',
        name: 'Iceland'
      },
      copyrightNotice: 'Iceland'
    },
    url,
    brand,
    name,
    offers: buildStructuredOfferData(priceForData, 'GBP', wasPriceforData),
    aggregateRating: buildStructuredAggregateRatingData(productRating, productReviewCount)
  }
}

export const buildProductListStructuredData = (
  products: ItemPrices[],
  pathname = ''
): StructuredProductList => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: getAppOrigin() + pathname,
    numberOfItems: products.length.toString(),
    itemListElement: products.map(({hit, lowestPrice, lowestWasPrice}) =>
      buildProductModelStructured(hit, lowestPrice ?? null, lowestWasPrice ?? null)
    )
  }
}

export const buildStructuredGroceryStoreData = (
  store: StoreDetailResponseItem,
  seasonalHours: SeasonalHours[] | null
): StructuredGroceryStore => {
  const appOrigin = getAppOrigin()

  return {
    '@context': 'https://schema.org',
    '@type': 'GroceryStore',
    '@id': appOrigin,
    name: `Iceland Foods (${store.name})`,
    url: `${appOrigin}/store-finder/store?StoreID=${store.id}`,
    telephone: store.phone.replace(/^0/, '+44'),
    priceRange: '££',
    address: {
      '@type': 'PostalAddress',
      streetAddress: [store.address1, store.address2].filter(Boolean).join(', '),
      addressLocality: store.city,
      postalCode: store.postalCode,
      addressCountry: 'GB'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: store.latitude,
      longitude: store.longitude
    },
    openingHoursSpecification: store.c_storeHoursJson.reduce((prev, curr) => {
      if (curr.isClosed) {
        prev.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: capitalize(curr.day) || curr.day
        })

        return prev
      }

      prev.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: capitalize(curr.day) || curr.day,
        opens: DateUtils.convert12HourTo24Hour(curr.open),
        closes: DateUtils.convert12HourTo24Hour(curr.close)
      })

      return prev
    }, [] as StructuredOpeningHoursSpecification[]),
    specialOpeningHoursSpecification: seasonalHours?.reduce((prev, curr) => {
      if (curr.isClosed && curr.validFrom && curr.validTo) {
        prev.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: curr.day,
          validFrom: curr.validFrom,
          validThrough: curr.validTo
        })
        return prev
      }

      if (curr.isClosed || !curr.open24HourTime || !curr.close24HourTime) {
        return prev
      }

      prev.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: curr.day,
        opens: curr.open24HourTime,
        closes: curr.close24HourTime,
        validFrom: curr.validFrom,
        validThrough: curr.validTo
      })

      return prev
    }, [] as StructuredOpeningHoursSpecification[]),
    parentOrganization: {
      '@type': 'Organization',
      '@id': appOrigin
    }
  }
}

export const buildHomepageStructuredData = (): StructuredOnlineStore => {
  const appOrigin = getAppOrigin()

  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    '@id': appOrigin,
    name: 'Iceland Foods',
    url: appOrigin,
    logo: `${appOrigin}/logo.svg`,
    sameAs: [
      'https://www.facebook.com/IcelandFoods',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+44-800-328-0800',
      contactType: 'customer service',
      areaServed: 'GB',
      availableLanguage: 'English',
    }
  }
}

export const buildWebsiteStructuredData = (): StructuredWebsite => {
  const appOrigin = getAppOrigin()

  return {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': appOrigin,
  url: appOrigin,
  name: 'Iceland Foods',
  potentialAction: [
        {
          '@type': 'SearchAction',
          name: 'Search for groceries online',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${appOrigin}/search?q={search_term_string}`,
          },
          query: 'required name=search_term_string',
        },
        {
          '@type': 'TradeAction',
          name: 'Order groceries online',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: appOrigin,
          },
        },
        {
          '@type': 'FindAction',
          name: 'Find an Iceland store near you',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${appOrigin}/store-finder`,
          },
        },
      ]
  }
}